/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { ActionType } from 'cio/models/action';
import { task } from 'ember-concurrency';
import { Measure } from 'cio/utils/metrics/common';
import { findNumerator, findDenominator, MeasureType } from 'cio/utils/metrics/measure';
import { Metric } from 'cio/utils/metrics/models/metric';
import DS from 'ember-data';
import { SafeString } from 'handlebars';
import SubscriptionCenterService from 'cio/services/subscription-center';
import { inject as service } from '@ember/service';
import pluralize from 'cio/utils/pluralize';
import { formatNumber } from 'cio/helpers/format-number';
import { measureLabel } from 'cio/helpers/measure-label';

interface RateTooltipArgs {
  measure: Measure;
  metric: Metric | DS.PromiseObject<Metric> | null;
  condition?: boolean;
  triggerClassNames?: string;
  contentClassNames?: string;
  actionType?: ActionType;
  isLoading?: boolean;
  totalAccessor?: 'total' | 'allTimeTotal' | 'rawTotal';
  rateAccessor?: 'rate' | 'allTimeRate' | 'rawRate';
  showRate?: boolean;
  direction?: string;
  extraContent?: SafeString;
}

export default class RateTooltip extends Component<RateTooltipArgs> {
  @service subscriptionCenter!: SubscriptionCenterService;
  @tracked metric: Metric | null = null;

  fetchMetric = task({ keepLatest: true }, async () => {
    if (this.args.metric != null) {
      this.metric = await this.args.metric;
    }
  });

  get direction() {
    return this.args.direction ?? 'top';
  }

  get isLoading() {
    return (this.args.isLoading ?? false) || this.fetchMetric.isRunning;
  }

  get condition() {
    return this.args.condition ?? true;
  }

  get totalAccessor(): 'total' | 'allTimeTotal' | 'rawTotal' {
    return this.args.totalAccessor ?? 'total';
  }

  get rateAccessor(): 'rate' | 'allTimeRate' | 'rawRate' {
    return this.args.rateAccessor ?? 'rate';
  }

  get rate() {
    return this.metric?.[this.args.measure]?.[this.rateAccessor];
  }

  get numerator() {
    return findNumerator(this.args.measure, MeasureType.DeliveryMetrics);
  }

  get numeratorTotal() {
    return this.metric?.[this.args.measure]?.[this.totalAccessor];
  }

  get denominatorTotal() {
    if (!this.denominator) {
      return;
    }

    return this.metric?.[this.denominator]?.[this.totalAccessor];
  }

  get denominator() {
    return findDenominator(this.args.measure, MeasureType.DeliveryMetrics, this.args.actionType);
  }

  get shouldShowDenominatorNotice() {
    return (
      this.denominator === 'delivered' &&
      this.numeratorTotal !== 0 &&
      (this.denominatorTotal === 0 || this.denominatorTotal == null)
    );
  }

  get contentClassNames() {
    let classes: string[] = [];

    if (this.args.contentClassNames != null) {
      classes = [this.args.contentClassNames];
    }

    if (this.shouldShowDenominatorNotice) {
      classes = [...classes, 'max-w-44'];
    }

    return classes.join(' ');
  }

  get summaryStatement() {
    let title = '';
    let numeratorTotal = parseInt(formatNumber([this.numeratorTotal]));
    if (this.args.measure === 'unsubscribed') {
      return (title += pluralize(numeratorTotal, 'unsubscribe', 'unsubscribes'));
    }
    if (this.args.measure === 'topic_unsubscribed') {
      return 'topic unsubscribes';
    }
    return (title += measureLabel([this.numerator, MeasureType.DeliveryMetrics])?.toLowerCase());
  }

  get subscriptionTopicMetrics() {
    if (!this.subscriptionCenter.initialized) {
      return [];
    }
    type MetricWithSubscriptionTopicMetrics = Metric & {
      perSubscriptionTopicMetrics: { subscriptionTopicId: string; value: string }[];
    };
    return (this.args.metric as MetricWithSubscriptionTopicMetrics)?.perSubscriptionTopicMetrics.map(
      (metric: Metric & { subscriptionTopicId: string; value: string }) => {
        let name = this.subscriptionCenter.topics.find((topic) => topic.id === metric.subscriptionTopicId)?.name;
        if (name == null) {
          name = `deleted topic_${metric.subscriptionTopicId}`;
        }

        return {
          ...metric,
          name,
        };
      },
    );
  }
}
